import React from 'react';
import { ChevronDown, ChevronRight, PackageMinus } from 'lucide-react';
import type { InventoryItem, RoastedBulkGroup } from '../../types/inventory';
import { formatCurrency, formatDecimal } from '../../utils/format';
import { RoastedBulkGroupItemsTable } from './RoastedBulkGroupItemsTable';
import { useLanguage } from '../../features/language/useLanguage';

interface RoastedBulkGroupsMobileProps {
  groups: RoastedBulkGroup[];
  statusFilter: 'all' | 'active' | 'inactive';
  expandedKeys: Set<string>;
  toggleGroup: (key: string) => void;
  currencySymbol: string;
  items: InventoryItem[];
  onEditItem?: (itemId: string) => void;
  onOpenHistory: (itemId: string) => void;
  onOpenRoastProfile: (context: { itemId: string; itemName: string; workOrderId?: string }) => void;
  onAdjustStock?: (group: RoastedBulkGroup) => void;
  canWrite?: boolean;
}

export const RoastedBulkGroupsMobile: React.FC<RoastedBulkGroupsMobileProps> = ({
  groups,
  statusFilter,
  expandedKeys,
  toggleGroup,
  currencySymbol,
  items,
  onEditItem,
  onOpenHistory,
  onOpenRoastProfile,
  onAdjustStock,
  canWrite,
}) => {
  const { t } = useLanguage();
  const statusByItemId = new Map(items.map(item => [item.id, item.status]));

  const renderItemStatus = (status?: string) => (
    <span
      className={`px-2 uppercase inline-flex text-xs leading-5 font-semibold rounded-lg ${
        status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
      }`}
    >
      {status === 'active' ? t('forms.common.statusActive') : t('forms.common.statusInactive')}
    </span>
  );

  return (
    <div className="md:hidden divide-y divide-gray-200">
      {groups.map(group => {
        const key = `${group.itemId}:${group.roastType}`;
        const isExpanded = expandedKeys.has(key);
        const itemStatus =
          group.items
            ?.map(groupItem => statusByItemId.get(groupItem.inventoryItemId) ?? groupItem.status)
            .find(Boolean) ?? statusByItemId.get(group.itemId);
        return (
          <div
            key={key}
            className="p-4 space-y-3 bg-white cursor-pointer"
            onClick={() => {
              if (onEditItem && canWrite) {
                onEditItem(group.itemId);
                return;
              }
              toggleGroup(key);
            }}
          >
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    toggleGroup(key);
                  }}
                  className="mt-0.5 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{group.itemName}</h3>
                  <p className="text-sm text-gray-500">{group.itemCode}</p>
                  <p className="text-xs text-gray-500">{t('workOrders.table.roastType')}: {group.roastType || t('settingsMcp.values.unknown')}</p>
                </div>
              </div>
              <div className="text-right text-sm text-gray-500">
                <div>{formatDecimal(group.totalStock, 3)} {t('inventory.history.unitKg')}</div>
                <div>
                  {formatCurrency(typeof group.averagePrice === 'number' ? group.averagePrice : 0, currencySymbol)}
                </div>
                <div className="mt-1">{renderItemStatus(itemStatus)}</div>
                {canWrite && onAdjustStock && (
                  <button
                    type="button"
                    data-test-id={`inventory-roasted-group-adjust-mobile-${group.itemId}-${group.roastType}`}
                    onClick={event => {
                      // The card's own onClick opens item edit; this must not.
                      event.stopPropagation();
                      onAdjustStock(group);
                    }}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-950 transition-colors hover:bg-primary-100"
                  >
                    <PackageMinus className="h-4 w-4" />
                    {t('inventory.roastedBulk.adjust.button')}
                  </button>
                )}
              </div>
            </div>

            {isExpanded && (
              <div className="mt-2 pt-2 border-t border-gray-100" onClick={event => event.stopPropagation()}>
                <RoastedBulkGroupItemsTable
                  group={group}
                  statusFilter={statusFilter}
                  currencySymbol={currencySymbol}
                  items={items}
                  onEditItem={onEditItem}
                  onOpenHistory={onOpenHistory}
                  compact
                  onOpenRoastProfile={onOpenRoastProfile}
                  canWrite={canWrite}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
